import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
} from "react";
import { jwtDecode } from "jwt-decode";
import { JobData as StaticJobData } from "../pages/detailjob/JobData";
import { JobListDataProps } from "../pages/detailjob/Section";
import { auth, db } from "../firebase";
import {
  collection,
  getDocs,
  doc,
  setDoc,
  deleteDoc,
  getDoc,
} from "firebase/firestore";
import {
  GoogleAuthProvider,
  onAuthStateChanged,
  signInWithCredential,
  signOut,
} from "firebase/auth";
import { toast } from "react-toastify";

interface User {
  email: string;
}

interface JobContextType {
  jobs: Record<string, JobListDataProps>;
  user: User | null;
  login: (credential: string) => Promise<boolean>;
  logout: () => Promise<void>;
  addJob: (slug: string, job: JobListDataProps) => Promise<void>;
  updateJob: (slug: string, job: JobListDataProps) => Promise<void>;
  deleteJob: (slug: string) => Promise<void>;
}

const JobContext = createContext<JobContextType | undefined>(undefined);

export const JobProvider = ({ children }: { children: ReactNode }) => {
  const [jobs, setJobs] = useState<Record<string, JobListDataProps>>({});
  const [user, setUser] = useState<User | null>(null);
  const [allowedDomains, setAllowedDomains] = useState<string[]>([]);
  const [allowedEmails, setAllowedEmails] = useState<string[]>([]);

  const CACHE_KEY_JOBS = "geargames_jobs_cache";
  const CACHE_KEY_AUTH = "geargames_auth_cache";
  const CACHE_DURATION = 10 * 60 * 1000; // 10 minutes

  // Load user from localStorage on mount for faster UI hydration.
  useEffect(() => {
    const storedUser = localStorage.getItem("cms_user");
    if (storedUser) {
      setUser(JSON.parse(storedUser));
    }
  }, []);

  // Keep CMS session aligned with Firebase Auth for Firestore rules.
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (firebaseUser) => {
      if (firebaseUser?.email) {
        const nextUser = { email: firebaseUser.email };
        setUser(nextUser);
        localStorage.setItem("cms_user", JSON.stringify(nextUser));
      } else {
        setUser(null);
        localStorage.removeItem("cms_user");
      }
    });

    return () => unsubscribe();
  }, []);

  // Helper to get cached data
  const getCachedData = (key: string) => {
    const cached = localStorage.getItem(key);
    if (cached) {
      const { data, timestamp } = JSON.parse(cached);
      if (Date.now() - timestamp < CACHE_DURATION) {
        return data;
      }
    }
    return null;
  };

  // Helper to set cached data
  const setCachedData = (key: string, data: any) => {
    localStorage.setItem(key, JSON.stringify({ data, timestamp: Date.now() }));
  };

  // Fetch Jobs and Auth Config from Firestore
  useEffect(() => {
    const fetchData = async () => {
      try {
        // --- Fetch Jobs ---
        const cachedJobs = getCachedData(CACHE_KEY_JOBS);
        if (cachedJobs) {
          console.log("Using cached jobs data");
          setJobs(cachedJobs);
        } else {
          console.log("Fetching jobs from Firestore");
          let jobsSnapshot;
          let jobsCollection;
          try {
            jobsCollection = collection(db, "jobs");

            jobsSnapshot = await getDocs(jobsCollection);
          } catch (error) {
            console.error("Error fetching jobs from Firestore:", error);
            throw error;
          }
          const jobsData: Record<string, JobListDataProps> = {};

          if (jobsSnapshot.empty) {
            console.log("No jobs in Firestore, using static data.");
            setJobs(StaticJobData);
            // We might not want to cache static fallback, or maybe we do?
            // Let's cache it so we don't keep asking firestore and getting empty.
          } else {
            jobsSnapshot.forEach((doc: any) => {
              jobsData[doc.id] = doc.data() as JobListDataProps;
            });
            setJobs(jobsData);
            setCachedData(CACHE_KEY_JOBS, jobsData);
          }
        }

        // --- Fetch Auth Config ---
        const cachedAuth = getCachedData(CACHE_KEY_AUTH);
        if (cachedAuth) {
          setAllowedDomains(cachedAuth.allowedDomains);
          setAllowedEmails(cachedAuth.allowedEmails);
        } else {
          const authDocRef = doc(db, "settings", "auth");
          const authDoc = await getDoc(authDocRef);
          if (authDoc.exists()) {
            const data = authDoc.data();
            const domains = data.allowedDomains || [];
            const emails = data.allowedEmails || [];
            setAllowedDomains(domains);
            setAllowedEmails(emails);
            setCachedData(CACHE_KEY_AUTH, {
              allowedDomains: domains,
              allowedEmails: emails,
            });
          } else {
            setAllowedDomains(["@geargames.com"]);
          }
        }
      } catch (error) {
        console.error("Error fetching data from Firestore:", error);
        // Fallback to static data on error
        // Only fallback if we don't have jobs yet (e.g. if cache failed but we are here)
        // If we successfully loaded cache for jobs but failed for auth, we should check `jobs` state...
        // actually easier to just set fallback if jobs state is empty or error occurred.
        // But since we split the logic, error could happen in either.
        // Ideally we should use separate try/catch or just be safe.
        // For now, let's keep robust fallback.
        setJobs(StaticJobData);
        setAllowedDomains(["@geargames.com"]);
      }
    };

    fetchData();
  }, []);

  const login = async (credential: string) => {
    try {
      const decoded: any = jwtDecode(credential);
      const email = decoded.email;

      if (email) {
        // Check if email domain is allowed
        const isDomainAllowed = allowedDomains.some((domain) =>
          email.endsWith(domain),
        );
        // Check if specific email is allowed
        const isEmailAllowed = allowedEmails.includes(email);

        if (!isDomainAllowed && !isEmailAllowed) {
          alert(`Access denied. Allowed domains: ${allowedDomains.join(", ")}`);
          return false;
        }

        const firebaseCredential = GoogleAuthProvider.credential(credential);
        await signInWithCredential(auth, firebaseCredential);

        const newUser = { email };
        setUser(newUser);
        localStorage.setItem("cms_user", JSON.stringify(newUser));
        toast.success("Login successful");
        return true;
      }
    } catch (error) {
      console.error("Login failed", error);
      toast.error("Login failed");
    }
    return false;
  };

  const logout = async () => {
    await signOut(auth);
    setUser(null);
    localStorage.removeItem("cms_user");
    toast.success("Logout successful");
  };

  const addJob = async (slug: string, job: JobListDataProps) => {
    try {
      await setDoc(doc(db, "jobs", slug), job);
      setJobs((prev) => {
        const newJobs = { ...prev, [slug]: job };
        setCachedData(CACHE_KEY_JOBS, newJobs); // Update cache
        return newJobs;
      });
    } catch (error) {
      console.error("Error adding job:", error);
      toast.error("Failed to add job to Firestore");
    }
  };

  const updateJob = async (slug: string, job: JobListDataProps) => {
    try {
      await setDoc(doc(db, "jobs", slug), job); // setDoc with merge:true or just overwrite
      setJobs((prev) => {
        const newJobs = { ...prev, [slug]: job };
        setCachedData(CACHE_KEY_JOBS, newJobs); // Update cache
        return newJobs;
      });
      toast.success("Job updated successfully");
    } catch (error) {
      console.error("Error updating job:", error);
      toast.error("Failed to update job in Firestore");
    }
  };

  const deleteJob = async (slug: string) => {
    try {
      await deleteDoc(doc(db, "jobs", slug));
      setJobs((prev) => {
        const newJobs = { ...prev };
        delete newJobs[slug];
        setCachedData(CACHE_KEY_JOBS, newJobs); // Update cache
        return newJobs;
      });
      toast.success("Job deleted successfully");
    } catch (error) {
      console.error("Error deleting job:", error);
      toast.error("Failed to delete job from Firestore");
    }
  };

  return (
    <JobContext.Provider
      value={{ jobs, user, login, logout, addJob, updateJob, deleteJob }}
    >
      {children}
    </JobContext.Provider>
  );
};

export const useJobContext = () => {
  const context = useContext(JobContext);
  if (!context) {
    throw new Error("useJobContext must be used within a JobProvider");
  }
  return context;
};
